import {
    differenceInCalendarDays,
    format,
    formatRelative,
    isPast,
    isThisYear,
    isToday,
    isYesterday,
} from 'date-fns'

export enum FormatString {
    // 14 Oct
    ShortLocalDate = 'd MMM',
    // 14 Oct 2021
    ShortLocalDate_Year = 'd MMM y',

    // 14 October
    LocalDate = 'd MMMM',
    // 14 October 2021
    LocalDate_Year = 'd MMMM y',

    // 12:00 AM
    LocalTime = 'p',

    // 14 Oct, 12:00 AM
    ShortLocalDateTime = 'd MMM, p',
    // 14 Oct 2021, 12:00 AM
    ShortLocalDateTime_Year = 'd MMM y, p',

    // 14 October, 12:00 AM
    LocalDateTime = 'd MMMM, p',
    // 14 October 2021, 12:00 AM
    LocalDateTime_Year = 'd MMMM y, p',

    // NB: Long DateTime formats are intended to be used in tooltips
    // That's why we provide there as much information as possible,
    // including seconds, the exact time zone and day of the week.

    // 14 Oct 2021, 8:00:00 PM (GMT+3)
    LongDateTime_TZ = 'd MMM y, pp (OO)',
    // Monday, 14 Oct 2021, 8:00:00 PM (GMT+3)
    LongDateTime_DoW_TZ = 'cccc, d MMM y, pp (OO)',
}

export function toShortAndLongDates(dateStr: string) {
    const date = new Date(dateStr)
    const shortDate = format(date, FormatString.ShortLocalDate_Year)
    const longDate = format(date, FormatString.LocalDate_Year)

    return [shortDate, longDate]
}

/**
 * For a `date` (today, from the past) returns a `string` in order of distance:
 * - "12:00 AM"    — for today, or
 * - "yesterday"   — for yesterday, or
 * - "Wednesday"   — for days of the last week (DoW), or
 * - "14 Oct"      — for this year's date, or
 * - "14 Oct 2021" — for later dates.
 *
 * NOTE: This fn is designed to only be used with dates from the past or today,
 *       but it mustn't fail for tomorrow and subsequent future dates, thus we
 *       return an empty string in this case.
 */
export function customFormatRelative(date: Date) {
    if (isToday(date)) {
        return format(date, FormatString.LocalTime)
    } else if (!isPast(date)) {
        return ''
    } else if (isYesterday(date)) {
        return 'yesterday'
    } else {
        const now = new Date()
        if (differenceInCalendarDays(now, date) < 7) {
            const fullRelStr = formatRelative(date, now, { weekStartsOn: 1 })
            return fullRelStr.split(' ')[1] // 'last Wednesday at 12:21 PM' -> 'Wednesday'
        } else if (isThisYear(date)) {
            return format(date, FormatString.ShortLocalDate)
        } else {
            return format(date, FormatString.ShortLocalDate_Year)
        }
    }
}
